import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Link,
  Popover,
  Skeleton,
  Typography,
} from "@mui/material";
import { getActiveOrganisation, useIdentity } from "contexts/identity-context";
import * as QueryKeys from "data";
import * as Routes from "routes";
import { UsageGraph } from "./usage-graph";
import { UsageTable } from "./usageTable";

// reactQuery
import { fetchSubscription, fetchUsage } from "data/queries";
import { useQuery } from "react-query";
import { resolveIdentityId } from "utils/identity";
import { DateRangePicker } from "./date-range-picker";
import { useState } from "react";
import {
  DateRange,
  DateRangeValidationError,
  PickerChangeHandlerContext,
} from "@mui/x-date-pickers-pro";
import dayjs, { Dayjs } from "dayjs";
import { unixDurationToDateRange } from "./utils-usage";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { ReactQueryErrorWrapper } from "components/shared/react-query-error-wrapper";

export const Usage = () => {
  // useQuery
  const [identityState] = useIdentity();
  const isUser = !getActiveOrganisation(identityState);
  const identityId = resolveIdentityId(identityState, isUser);

  const subscriptionQuery = useQuery([QueryKeys.subscription, identityId], () =>
    fetchSubscription(isUser, identityState)
  );

  const isFreemium: boolean =
    subscriptionQuery.isSuccess &&
    subscriptionQuery.data.activeSubscription.plan.name === "Freemium";

  const [startTime, setStartTime] = useState(
    dayjs().subtract(30, "day").unix() * 1000
  );
  const [endTime, setEndTime] = useState(dayjs().unix() * 1000);

  const [label, setLabel] = useState(
    unixDurationToDateRange(startTime, endTime).toString()
  );
  const dailyUsageQuery = useQuery(
    [QueryKeys.dailyUsage, identityId, startTime, endTime],
    () => fetchUsage(isUser, identityState, startTime, endTime),
    { enabled: !!subscriptionQuery.data }
  );

  const isLoading = subscriptionQuery.isLoading || dailyUsageQuery.isLoading;
  const isError = subscriptionQuery.isError || dailyUsageQuery.isError;
  const isSuccess = subscriptionQuery.isSuccess && dailyUsageQuery.isSuccess;
  const [open, setOpen] = useState(false);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setOpen(!open);
  };
  const handleClose = () => {
    setOpen(false);
    setAnchorEl(null);
  };

  const handleDateRangeChange = (
    value: DateRange<Dayjs>,
    context: PickerChangeHandlerContext<DateRangeValidationError>
  ) => {
    if (value[0] && value[1]) {
      const st = value[0].unix() * 1000;
      const et = value[1].unix() * 1000;
      setStartTime(st);
      setEndTime(et);
      handleClose();
      if (context.shortcut) {
        setLabel(context.shortcut.label);
      } else {
        const startDay = dayjs(value[0]).format("MMM DD");
        const endDay = dayjs(value[1]).format("MMM DD");
        setLabel(`${startDay} - ${endDay}`);
      }
    }
  };

  return (
    <>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          gap: "24px",
          position: "relative",
        }}
      >
        <ReactQueryErrorWrapper
          queries={[subscriptionQuery, dailyUsageQuery]}
          mutations={[]}
        />

        {/* Div Total Credits and Overage */}
        <div style={{ display: "flex", height: "90px", gap: "20px" }}>
          <Card
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              paddingRight: 2,
              width: "50%",
            }}
          >
            <CardHeader
              sx={{ width: "60%" }}
              title="Set Date range"
              subheader={
                isFreemium && (
                  <Typography variant="body2">
                    Freemium can only report to a maximum of 90 days in the
                    past.
                  </Typography>
                )
              }
            ></CardHeader>
            <Button
              size="small"
              variant="contained"
              onClick={handleClick}
              startIcon={<CalendarTodayIcon />}
              endIcon={<KeyboardArrowDownIcon />}
            >
              {label}
            </Button>
            <Popover
              open={open}
              anchorEl={anchorEl}
              onClose={handleClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
            >
              <DateRangePicker onChange={handleDateRangeChange} />
            </Popover>
          </Card>
          <Card
            style={{
              marginBottom: "auto",
              display: "flex",
              flexDirection: "column",
              width: "20%",
            }}
          >
            {isLoading && (
              <>
                <CardContent style={{ minHeight: "90px" }}>
                  <Box>
                    <Skeleton variant="rectangular" height="56px" />
                  </Box>
                </CardContent>
              </>
            )}

            {isSuccess && (
              <>
                <CardHeader
                  style={{ minHeight: "90px" }}
                  variant="primary"
                  title={dailyUsageQuery.data.totalCredits.toLocaleString(
                    "en-US",
                    { minimumFractionDigits: 1 }
                  )}
                  subheader="Total Credits"
                  subheaderTypographyProps={{ variant: "body2" }}
                />
              </>
            )}
          </Card>
          <Card
            style={{
              width: "45%",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "0  16px 0 0",
            }}
          >
            <CardHeader
              variant="primary"
              title={"Increase your Overage"}
              subheader="No one likes dropping API calls."
              subheaderTypographyProps={{ variant: "body2" }}
            />
            <Button
              LinkComponent={Link}
              href={
                getActiveOrganisation(identityState)
                  ? Routes.orgOverage
                  : Routes.userOverage
              }
              variant="contained"
              color="primary"
              size="small"
              style={{ height: "36px" }}
            >
              EDIT OVERAGE
            </Button>
          </Card>
        </div>
        {/* Div Date Range and Graph Usage */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            gap: "24px",
            height: "500px",
            position: "relative",
          }}
        >
          <div>
            <UsageGraph startTime={startTime} endTime={endTime} />
          </div>
          <div>
            <UsageTable startTime={startTime} endTime={endTime} />
          </div>
        </div>
      </div>
    </>
  );
};
